/**
 * Particle pools (plan §2.2 `PARTICLE_ATTR`): one `Mesh` per `VfxMaterialKey` with an
 * `InstancedBufferGeometry` (unit quad + the six instanced attributes), `instanceCount` = instances drawn,
 * identity transform at the render origin, `frustumCulled = false`. The asset lane's `VfxMaterials`
 * billboard or stretch the quad from the attributes.
 *
 * Each pool holds a CPU `ParticleStore` (float64 world positions, D-011) simulated by emitters.ts, and
 * every frame writes render-space attributes for its live particles only. Pools also take "direct"
 * instances for this frame only (projectile tracers), appended after the simulated particles.
 *
 * Frame protocol (allocation-free, D-014): `stepParticleSystem(now)` → `beginParticleFrame(cam)` →
 * any `pushInstance` → `endParticleFrame()`.
 */
import {
  DynamicDrawUsage,
  Group,
  InstancedBufferAttribute,
  InstancedBufferGeometry,
  Mesh,
  NormalBlending,
  PlaneGeometry,
  type BufferAttribute,
  type Material,
} from 'three/webgpu'
import type { Vec3 } from '../../core/math'
import { createRng, type Rng } from '../../core/rng'
import {
  PARTICLE_ATTR,
  PARTICLE_ATTR_SIZES,
  VFX_MATERIAL_KEYS,
  type VfxEffectId,
  type VfxEmitterSpec,
  type VfxMaterialKey,
  type VfxRecipeBook,
} from '../../render/contracts'
import {
  burstCount,
  clearParticles,
  createParticleStore,
  emitParticles,
  particleAge,
  stepParticles,
  type ParticleStore,
} from './emitters'

type AttrName = keyof typeof PARTICLE_ATTR

export interface ParticleAttributes {
  pos: InstancedBufferAttribute
  age: InstancedBufferAttribute
  seed: InstancedBufferAttribute
  tint: InstancedBufferAttribute
  size: InstancedBufferAttribute
  dir: InstancedBufferAttribute
}

export interface ParticlePool {
  readonly key: VfxMaterialKey
  readonly mesh: Mesh<InstancedBufferGeometry, Material>
  /** Simulated particles (capacity = the pool's share of the quality cap). */
  store: ParticleStore
  geometry: InstancedBufferGeometry
  attrs: ParticleAttributes
  /** Instances written this frame (simulated + direct). */
  count: number
}

function instanced(capacity: number, name: AttrName): InstancedBufferAttribute {
  const itemSize = PARTICLE_ATTR_SIZES[name]
  const a = new InstancedBufferAttribute(new Float32Array(capacity * itemSize), itemSize)
  a.setUsage(DynamicDrawUsage)
  return a
}

function buildGeometry(capacity: number): { geometry: InstancedBufferGeometry; attrs: ParticleAttributes } {
  // Each geometry owns its quad: disposing a geometry frees its attributes' GPU buffers, so none are shared.
  // PlaneGeometry(1, 1) is the contract quad: 4 vertices at (±0.5, ±0.5, 0) with uv, 2 triangles.
  const q = new PlaneGeometry(1, 1)
  const geometry = new InstancedBufferGeometry()
  geometry.setIndex(q.index)
  geometry.setAttribute('position', q.getAttribute('position') as BufferAttribute)
  geometry.setAttribute('uv', q.getAttribute('uv') as BufferAttribute)
  const attrs: ParticleAttributes = {
    pos: instanced(capacity, 'pos'),
    age: instanced(capacity, 'age'),
    seed: instanced(capacity, 'seed'),
    tint: instanced(capacity, 'tint'),
    size: instanced(capacity, 'size'),
    dir: instanced(capacity, 'dir'),
  }
  geometry.setAttribute(PARTICLE_ATTR.pos, attrs.pos)
  geometry.setAttribute(PARTICLE_ATTR.age, attrs.age)
  geometry.setAttribute(PARTICLE_ATTR.seed, attrs.seed)
  geometry.setAttribute(PARTICLE_ATTR.tint, attrs.tint)
  geometry.setAttribute(PARTICLE_ATTR.size, attrs.size)
  geometry.setAttribute(PARTICLE_ATTR.dir, attrs.dir)
  geometry.instanceCount = 0
  return { geometry, attrs }
}

export function createParticlePool(key: VfxMaterialKey, material: Material, capacity: number): ParticlePool {
  const { geometry, attrs } = buildGeometry(capacity)
  const mesh = new Mesh(geometry, material)
  mesh.name = `fx:${key}`
  mesh.frustumCulled = false
  mesh.matrixAutoUpdate = false
  mesh.visible = false
  // Normal-blended smoke draws before the additive glows, so fire and sparks read on top of it.
  mesh.renderOrder = material.blending === NormalBlending ? 0 : 1
  return { key, mesh, store: createParticleStore(capacity), geometry, attrs, count: 0 }
}

/** Instance capacity of the pool's GPU buffers. */
export function poolCapacity(pool: ParticlePool): number {
  return pool.store.capacity
}

/** Rebuilds the pool at a new capacity (quality change; allocates). Live particles are dropped. */
export function resizeParticlePool(pool: ParticlePool, capacity: number): void {
  if (capacity === pool.store.capacity) {
    clearParticles(pool.store)
    return
  }
  const old = pool.geometry
  const { geometry, attrs } = buildGeometry(capacity)
  pool.geometry = geometry
  pool.attrs = attrs
  pool.mesh.geometry = geometry
  pool.store = createParticleStore(capacity)
  pool.count = 0
  pool.mesh.visible = false
  old.dispose()
}

/**
 * Appends one instance for this frame. Render-space centre (rx, ry, rz), normalized age, seed, tint,
 * width, streak length and unit direction. Returns false when the pool is full.
 */
export function pushInstance(
  pool: ParticlePool,
  rx: number,
  ry: number,
  rz: number,
  age: number,
  seed: number,
  r: number,
  g: number,
  b: number,
  width: number,
  length: number,
  dx: number,
  dy: number,
  dz: number,
): boolean {
  const i = pool.count
  if (i >= pool.store.capacity) return false
  const a = pool.attrs
  const p = a.pos.array as Float32Array
  const t = a.tint.array as Float32Array
  const s = a.size.array as Float32Array
  const d = a.dir.array as Float32Array
  const i3 = i * 3
  const i2 = i * 2
  p[i3] = rx
  p[i3 + 1] = ry
  p[i3 + 2] = rz
  ;(a.age.array as Float32Array)[i] = age
  ;(a.seed.array as Float32Array)[i] = seed
  t[i3] = r
  t[i3 + 1] = g
  t[i3 + 2] = b
  s[i2] = width
  s[i2 + 1] = length
  d[i3] = dx
  d[i3 + 1] = dy
  d[i3 + 2] = dz
  pool.count = i + 1
  return true
}

/**
 * Starts the pool's frame: writes every live simulated particle in render space relative to the float64
 * render origin `cam` (D-011). A streak is centred half its length ahead of an anchored particle (it grows
 * forward from a muzzle) or behind a moving one (it trails its head).
 */
export function beginPoolFrame(pool: ParticlePool, cam: Vec3): void {
  pool.count = 0
  const s = pool.store
  for (let i = 0; i < s.live; i++) {
    const dx = s.dx[i]
    const dy = s.dy[i]
    const dz = s.dz[i]
    const len = s.stretch[i]
    const off = len > 0 ? (s.anchored[i] === 1 ? 0.5 * len : -0.5 * len) : 0
    pushInstance(
      pool,
      Math.fround(s.x[i] - cam.x + dx * off),
      Math.fround(s.y[i] - cam.y + dy * off),
      Math.fround(s.z[i] - cam.z + dz * off),
      particleAge(s, i),
      s.seed[i],
      s.tr[i],
      s.tg[i],
      s.tb[i],
      s.size[i],
      len,
      dx,
      dy,
      dz,
    )
  }
}

/** Ends the pool's frame: sets `instanceCount`, uploads when anything is drawn, hides an empty pool. */
export function endPoolFrame(pool: ParticlePool): void {
  const n = pool.count
  pool.geometry.instanceCount = n
  pool.mesh.visible = n > 0
  if (n === 0) return
  const a = pool.attrs
  a.pos.needsUpdate = true
  a.age.needsUpdate = true
  a.seed.needsUpdate = true
  a.tint.needsUpdate = true
  a.size.needsUpdate = true
  a.dir.needsUpdate = true
}

// ---------------------------------------------------------------------------------------------------------
// The system: every pool, the recipes and the seeded RNG.

export interface ParticleSystem {
  readonly pools: Readonly<Record<VfxMaterialKey, ParticlePool>>
  /** The pools in `VFX_MATERIAL_KEYS` order. */
  readonly list: readonly ParticlePool[]
  /** Parent of every pool mesh; add it to the scene at the render origin. */
  readonly root: Group
  readonly recipes: VfxRecipeBook
  /** Seeded effects RNG (separate from the sim's, so effects never change the simulation). */
  readonly rng: Rng
  /** Sim time the particles were last stepped to. */
  time: number
}

export function createParticleSystem(
  materials: Readonly<Record<VfxMaterialKey, Material>>,
  recipes: VfxRecipeBook,
  capacities: Readonly<Record<VfxMaterialKey, number>>,
  seed: number,
): ParticleSystem {
  const root = new Group()
  root.name = 'fx:particles'
  root.matrixAutoUpdate = false
  const pools = {} as Record<VfxMaterialKey, ParticlePool>
  const list: ParticlePool[] = []
  for (const key of VFX_MATERIAL_KEYS) {
    const pool = createParticlePool(key, materials[key], capacities[key])
    pools[key] = pool
    list.push(pool)
    root.add(pool.mesh)
  }
  return { pools, list, root, recipes, rng: createRng(seed), time: 0 }
}

/** New capacities per pool (quality change). Allocates; live particles are dropped. */
export function setParticleCapacities(sys: ParticleSystem, capacities: Readonly<Record<VfxMaterialKey, number>>): void {
  for (let i = 0; i < sys.list.length; i++) resizeParticlePool(sys.list[i], capacities[sys.list[i].key])
}

/** Emits `n` particles of one emitter spec (see `emitParticles`). */
export function emitSpec(
  sys: ParticleSystem,
  spec: VfxEmitterSpec,
  n: number,
  x: number,
  y: number,
  z: number,
  dx: number,
  dy: number,
  dz: number,
  svx: number,
  svy: number,
  svz: number,
  time: number,
): void {
  if (n <= 0) return
  emitParticles(sys.pools[spec.material].store, spec, n, x, y, z, dx, dy, dz, svx, svy, svz, time, sys.rng)
}

/**
 * Runs effect `effect`'s recipe once at float64 world (x, y, z): every emitter bursts `count` particles
 * around direction (dx, dy, dz) with the source velocity (svx, svy, svz), born at sim time `time`.
 */
export function emitEffect(
  sys: ParticleSystem,
  effect: VfxEffectId,
  x: number,
  y: number,
  z: number,
  dx: number,
  dy: number,
  dz: number,
  svx: number,
  svy: number,
  svz: number,
  time: number,
): void {
  const specs = sys.recipes[effect]
  for (let e = 0; e < specs.length; e++) {
    const spec = specs[e]
    emitSpec(sys, spec, burstCount(spec.count, sys.rng), x, y, z, dx, dy, dz, svx, svy, svz, time)
  }
}

/** Advances every pool's particles to sim time `now` (never backwards). */
export function stepParticleSystem(sys: ParticleSystem, now: number): void {
  if (now < sys.time) now = sys.time
  sys.time = now
  for (let i = 0; i < sys.list.length; i++) stepParticles(sys.list[i].store, now)
}

/** Writes the simulated particles of every pool for this frame (render origin `cam`). */
export function beginParticleFrame(sys: ParticleSystem, cam: Vec3): void {
  for (let i = 0; i < sys.list.length; i++) beginPoolFrame(sys.list[i], cam)
}

export function endParticleFrame(sys: ParticleSystem): void {
  for (let i = 0; i < sys.list.length; i++) endPoolFrame(sys.list[i])
}

/** Removes every particle (new game, sim clock reset). */
export function clearParticleSystem(sys: ParticleSystem, time: number): void {
  for (let i = 0; i < sys.list.length; i++) {
    clearParticles(sys.list[i].store)
    sys.list[i].count = 0
  }
  sys.time = time
}

/** Live simulated particles in all pools (perf overlay). */
export function liveParticles(sys: ParticleSystem): number {
  let n = 0
  for (let i = 0; i < sys.list.length; i++) n += sys.list[i].store.live
  return n
}

/** Instances drawn this frame in all pools (simulated + tracers). */
export function drawnInstances(sys: ParticleSystem): number {
  let n = 0
  for (let i = 0; i < sys.list.length; i++) n += sys.list[i].count
  return n
}

export function disposeParticleSystem(sys: ParticleSystem): void {
  sys.root.removeFromParent()
  for (let i = 0; i < sys.list.length; i++) sys.list[i].geometry.dispose()
}
